import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface TrainerProfileCompletionBarProps {
  trainer: any;
  trainerId: string;
}

export const TrainerProfileCompletionBar = ({ trainer, trainerId }: TrainerProfileCompletionBarProps) => {
  const navigate = useNavigate();

  const completionFactors = [
    { name: "Información básica", completed: !!(trainer.bio && trainer.full_name) },
    { name: "Experiencia", completed: !!(trainer.years_experience && trainer.years_experience > 0) },
    { name: "Especialidades (3+)", completed: !!(trainer.specialty && trainer.specialty.length >= 3) },
    { name: "Tarifa por hora", completed: !!(trainer.hourly_rate) },
    { name: "Galería (3+ fotos)", completed: !!(trainer.gallery_images && trainer.gallery_images.length >= 3) },
    { name: "Video presentación", completed: !!(trainer.video_intro_url) },
    { name: "Certificaciones", completed: !!(trainer.certifications && trainer.certifications.length > 0) },
    { name: "Logros deportivos", completed: !!(trainer.achievements && trainer.achievements.length > 0) },
    { name: "Filosofía entrenamiento", completed: !!(trainer.teaching_philosophy) },
    { name: "Idiomas", completed: !!(trainer.languages && trainer.languages.length > 0) },
  ];

  const completedCount = completionFactors.filter(f => f.completed).length;
  const completionPercentage = Math.round((completedCount / completionFactors.length) * 100);
  const visibilityBoost = Math.round(completionPercentage / 2);

  if (completionPercentage >= 90) {
    return null;
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-full">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Aumenta tu Visibilidad</h3>
              <p className="text-xs text-muted-foreground">
                Tu perfil está {completionPercentage}% completo
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">+{visibilityBoost}%</div>
            <p className="text-xs text-muted-foreground">potencial</p>
          </div>
        </div>

        <div className="relative w-full bg-muted rounded-full h-3 mb-4 overflow-hidden">
          <div 
            className="bg-gradient-primary h-3 rounded-full transition-all duration-500 relative"
            style={{ width: `${completionPercentage}%` }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-semibold text-foreground/70">
              {completionPercentage}%
            </span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <p className="text-xs text-muted-foreground">
            <strong>Completa tu perfil para:</strong>
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Más contactos (+{visibilityBoost}%)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Mayor credibilidad</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Mejor posicionamiento</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Más confianza</span>
            </div>
          </div>
        </div>

        <Button
          onClick={() => navigate(`/edit-trainer/${trainerId}`)}
          className="w-full"
          size="sm"
        >
          Completar Perfil
        </Button>

        {completionPercentage < 100 && (
          <div className="mt-3 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
            <strong>Falta:</strong>{" "}
            {completionFactors
              .filter(f => !f.completed)
              .slice(0, 3)
              .map(f => f.name)
              .join(", ")}
            {completionFactors.filter(f => !f.completed).length > 3 && "..."}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
